import React from "react";
import { Phone, MapPin, Mail } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/site";

export default function Footer() {
  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-slate-950 text-white pt-20 pb-8 overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-50">
        <div className="absolute -top-40 -left-20 w-96 h-96 rounded-full bg-brand-ocean/40 blur-[120px]" />
        <div className="absolute -bottom-40 -right-10 w-96 h-96 rounded-full bg-brand-orange/30 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Three-column layout: Brand | Explore | Contact */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-12 gap-10">
          {/* Brand — left */}
          <div className="md:col-span-1 lg:col-span-5">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-2xl bg-white p-1.5">
                <img
                  src={BRAND.logoUrl}
                  alt="Happy Journey 4U"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                <div className="font-playfair text-2xl font-bold leading-none">
                  Happy Journey <span className="text-brand-orange">4U</span>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/55 mt-1.5">
                  Established {BRAND.established}
                </div>
              </div>
            </div>
            <p className="mt-5 text-white/65 leading-relaxed max-w-sm text-sm">
              Operated by <em>{BRAND.legalName}</em>, led by {BRAND.owner}. From
              the heart of Burhanpur, crafting personalised journeys across
              India and the world.
            </p>

            {/* Social — visually muted with tooltip, not dead expectations */}
            <div className="mt-6 flex gap-3 items-center">
              <span className="text-xs text-white/40 uppercase tracking-[0.15em]">
                Follow us
              </span>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — coming soon"
                data-testid="social-instagram"
                title="Instagram — link coming soon"
                className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:text-white/70 hover:border-white/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
              >
                {/* Instagram SVG icon */}
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — coming soon"
                data-testid="social-facebook"
                title="Facebook — link coming soon"
                className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:text-white/70 hover:border-white/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
              >
                {/* Facebook SVG icon */}
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Explore links — center */}
          <div className="md:col-span-1 lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-orange font-semibold">
              Explore
            </div>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => handleNav(l.id)}
                    data-testid={`footer-nav-${l.id}`}
                    className="text-white/70 hover:text-brand-orange transition-colors text-sm focus-visible:outline-none focus-visible:underline"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact — right */}
          <div className="md:col-span-1 lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-orange font-semibold">
              Contact
            </div>
            <ul className="mt-5 space-y-4">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                <span className="text-white/70 leading-relaxed text-sm">
                  {BRAND.address}
                </span>
              </li>
              {BRAND.phones.map((p) => (
                <li key={p.number} className="flex gap-3">
                  <Phone className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                  <a
                    href={`tel:${p.number.replace(/\s/g, "")}`}
                    className="text-white/70 hover:text-brand-orange transition-colors text-sm focus-visible:outline-none focus-visible:underline"
                  >
                    {p.number}
                  </a>
                </li>
              ))}
              <li className="flex gap-3">
                <Mail className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                <a
                  href={`mailto:${BRAND.email}`}
                  className="text-white/70 hover:text-brand-orange transition-colors text-sm focus-visible:outline-none focus-visible:underline"
                >
                  {BRAND.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider + bottom bar */}
        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-white/45">
            © {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.
          </div>
          <div className="text-xs text-white/30 italic">
            Crafted with care in Burhanpur
          </div>
        </div>
      </div>
    </footer>
  );
}
